import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ROLE_LABELS, errMsg } from './consoleUtils';

const PageGroup = ({ page, checked, toggle, togglePage }) => {
  const keys = [page.key, ...page.tabs.map(([t]) => `${page.key}.${t}`)];
  const allOn = keys.every(k => checked.has(k));
  return (
    <div className="border border-border/40 rounded-md p-3 space-y-2" data-testid={`perm-group-${page.key}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={checked.has(page.key)} onChange={() => toggle(page.key)} data-testid={`perm-${page.key}`} />
          {page.label}
        </label>
        {page.tabs.length > 0 && (
          <button className="text-[11px] text-primary hover:underline" onClick={() => togglePage(keys, !allOn)} data-testid={`perm-all-${page.key}`}>
            {allOn ? 'Untick all' : 'Tick all'}
          </button>
        )}
      </div>
      {page.tabs.length > 0 && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-1.5 pl-6 ${checked.has(page.key) ? '' : 'opacity-50'}`}>
          {page.tabs.map(([t, label]) => {
            const k = `${page.key}.${t}`;
            return (
              <label key={k} className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={checked.has(k)} onChange={() => toggle(k)} data-testid={`perm-${k.replace(/\./g, '-')}`} />
                {label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PermissionsTab = ({ meta, accounts, reload, userId, setUserId }) => {
  const account = accounts.find(a => a.id === userId);
  const [checked, setChecked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => { setChecked(new Set(account?.permissions || [])); }, [account]);

  const toggle = (k) => {
    const n = new Set(checked);
    n.has(k) ? n.delete(k) : n.add(k);
    setChecked(n);
  };
  const togglePage = (keys, on) => {
    const n = new Set(checked);
    keys.forEach(k => (on ? n.add(k) : n.delete(k)));
    setChecked(n);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/console/users/${userId}/permissions`, { permissions: [...checked] });
      toast.success('Permissions saved — they apply on the employee\'s next page load');
      reload();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-border/40" data-testid="console-permissions-tab">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <select className="flex h-9 w-full sm:w-80 rounded-md border border-input bg-background px-3 text-sm"
            value={userId} onChange={e => setUserId(e.target.value)} data-testid="select-permission-account">
            <option value="">Select an account</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.full_name} — {ROLE_LABELS[a.role] || a.role}</option>)}
          </select>
          {account && (
            <>
              <Button variant="outline" size="sm" onClick={() => setChecked(new Set(meta.role_templates[account.role] || []))} data-testid="btn-reset-role-defaults">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> {ROLE_LABELS[account.role]} defaults
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="btn-save-permissions">
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save permissions'}
              </Button>
            </>
          )}
        </div>
        {!account ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Choose an account to edit what pages and tabs it can open.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">Unticking a page hides it from the sidebar. Unticking a tab hides that tab inside the page. Some pages still apply their own role rules (e.g. Finance is Principal-only).</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {meta.catalog.map(p => <PageGroup key={p.key} page={p} checked={checked} toggle={toggle} togglePage={togglePage} />)}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
